import Point from '@mapbox/point-geometry';
import assert from '../../src/style-spec/util/assert';
import earcut from 'earcut';
import {mat4, vec3} from 'gl-matrix';
import {Aabb} from '../../src/util/primitives';
import Color from '../../src/style-spec/util/color';
import {TriangleIndexArray,
    ModelLayoutArray,
    NormalLayoutArray,
    TexcoordLayoutArray,
    TexcoordNormalizedLayoutArray,
    Color3fLayoutArray,
    Color4fLayoutArray,
    FeatureVertexArray
} from '../../src/data/array_types';
import {loadGLTF, GLTF_TO_ARRAY_TYPE, GLTF_COMPONENTS} from '../util/loaders';
import {base64DecToArr} from '../../src/util/util';
import TriangleGridIndex from '../../src/util/triangle_grid_index';
import Model, {HEIGHTMAP_DIM, PartIndices} from '../data/model';
import {ModelBVH} from './model_bvh';

import type {vec2} from 'gl-matrix';
import type {Class} from '../../src/types/class';
import type {Footprint} from '../util/conflation';
import type {TextureImage} from '../../src/render/texture';
import type {GLTF, GLTFNode, GLTFAccessor, GLTFPrimitive} from '../util/loaders';
import type {RequestParameters} from '../../src/util/ajax';
import type {Mesh, ModelNode, Material, MaterialDescription, ModelTexture, Sampler, AreaLight, PbrMetallicRoughness} from '../data/model';

function convertTextures(gltf: GLTF, images: Array<TextureImage>): Array<ModelTexture> {
    const textures: ModelTexture[] = [];
    const gl = WebGL2RenderingContext;
    if (gltf.json.textures) {
        for (const textureDesc of gltf.json.textures) {
            const sampler: Sampler = {
                magFilter: gl.LINEAR,
                minFilter: gl.NEAREST,
                wrapS: gl.REPEAT,
                wrapT: gl.REPEAT
            };
            if (textureDesc.sampler !== undefined) Object.assign(sampler, gltf.json.samplers[textureDesc.sampler]);
            textures.push({
                image: images[textureDesc.source],
                sampler,
                uploaded: false
            });
        }
    }
    return textures;
}

function convertMaterial(materialDesc: Partial<MaterialDescription>, textures: Array<ModelTexture>): Material {
    const {
        emissiveFactor = [0, 0, 0],
        alphaMode = 'OPAQUE',
        alphaCutoff = 0.5,
        normalTexture,
        occlusionTexture,
        emissiveTexture,
        doubleSided,
        name
    } = materialDesc;

    const {
        baseColorFactor = [1, 1, 1, 1],
        metallicFactor = 1.0,
        roughnessFactor = 1.0,
        baseColorTexture,
        metallicRoughnessTexture
    } = materialDesc.pbrMetallicRoughness || {};

    const modelOcclusionTexture = occlusionTexture ? textures[occlusionTexture.index] : undefined;
    // Supporting texture transform only for occlusion (mbx landmarks)
    // Check if KHR_Texture_transform is set.
    if (occlusionTexture && occlusionTexture.extensions && occlusionTexture.extensions['KHR_texture_transform'] && modelOcclusionTexture) {
        const transform = occlusionTexture.extensions['KHR_texture_transform'];
        modelOcclusionTexture.offsetScale = [transform.offset[0], transform.offset[1], transform.scale[0], transform.scale[1]];
    }

    return {
        name,
        pbrMetallicRoughness: {
            baseColorFactor: new Color(...baseColorFactor as [number, number, number, number]),
            metallicFactor,
            roughnessFactor,
            baseColorTexture: baseColorTexture ? textures[baseColorTexture.index] : undefined,
            metallicRoughnessTexture: metallicRoughnessTexture ? textures[metallicRoughnessTexture.index] : undefined
        },
        doubleSided,
        emissiveFactor: new Color(...emissiveFactor),
        alphaMode,
        alphaCutoff,
        normalTexture: normalTexture ? textures[normalTexture.index] : undefined,
        occlusionTexture: modelOcclusionTexture,
        emissionTexture: emissiveTexture ? textures[emissiveTexture.index] : undefined,
        defined: materialDesc.defined === undefined // just to make the rendertests the same than native
    };
}

function getNormalizedScale(arrayType: Class<ArrayBufferView>) {
    switch (arrayType) {
    case Int8Array:
        return 1 / 127;
    case Uint8Array:
        return 1 / 255;
    case Int16Array:
        return 1 / 32767;
    case Uint16Array:
        return 1 / 65535;
    default:
        return 1;
    }
}

function getBufferData(gltf: GLTF, accessor: GLTFAccessor): Uint32Array | Float32Array {
    const bufferView = gltf.json.bufferViews[accessor.bufferView];
    const buffer = gltf.buffers[bufferView.buffer];
    const offset = (accessor.byteOffset || 0) + (bufferView.byteOffset || 0);
    const ArrayType = GLTF_TO_ARRAY_TYPE[accessor.componentType] as Uint32ArrayConstructor | Float32ArrayConstructor;
    const itemBytes = GLTF_COMPONENTS[accessor.type] * ArrayType.BYTES_PER_ELEMENT;

    const stride: number = (bufferView.byteStride && bufferView.byteStride !== itemBytes) ?
        bufferView.byteStride / ArrayType.BYTES_PER_ELEMENT :
        GLTF_COMPONENTS[accessor.type];

    const bufferData = new ArrayType(buffer, offset, accessor.count * stride);
    return bufferData;
}

// The tiler packs a vertex color and a feature id into the two halves of one 32-bit word, but V1 and
// V2 tiles use opposite halves. Store them the way the model shader reads a_feature: color in .x,
// feature id in .y. The first door color is kept while the words are read, so that the door lights
// can be styled without walking the array again.
function setFeatureData(gltf: GLTF, accessor: GLTFAccessor, swapHalves: boolean, mesh: Mesh) {
    const data = getBufferData(gltf, accessor);
    // V2 encodes the word as a float, so it indexes per vertex. V1 stores it as raw bytes, which the
    // accessor describes as two uint16 components, so it has to be read back as one word per vertex.
    const words = data instanceof Float32Array ? data : new Uint32Array(data.buffer, data.byteOffset, accessor.count);
    const featureArray = new FeatureVertexArray();
    featureArray.reserveExact(accessor.count);
    let doorVertexColor = -1;
    for (let i = 0; i < accessor.count; i++) {
        const word = swapHalves ? (words[i] >>> 16) | (words[i] << 16) : words[i];
        const color = word & 0xffff;
        const id = word >>> 16;
        doorVertexColor = doorVertexColor < 0 && (id & 0xf) === PartIndices.door ? color : doorVertexColor;
        featureArray.emplaceBack(color, id);
    }
    mesh.featureArray = featureArray;
    if (doorVertexColor >= 0) {
        mesh.doorVertexColor = doorVertexColor;
    }
}

// Copies one accessor into a destination view. Normalized destinations hold floats in [0, 1] /
// [-1, 1]; raw destinations keep the source's integers and let the vertex fetch normalize them.
function setArrayData(gltf: GLTF, accessor: GLTFAccessor, dest: Float32Array | Uint16Array, buffer: ArrayLike<number>, normalize: boolean = true) {
    const ArrayType = GLTF_TO_ARRAY_TYPE[accessor.componentType];
    const components = GLTF_COMPONENTS[accessor.type];
    assert(dest.length === accessor.count * components);

    const norm = normalize ? getNormalizedScale(ArrayType) : 1;

    const bufferView = gltf.json.bufferViews[accessor.bufferView];

    const numElements = bufferView.byteStride ? bufferView.byteStride / ArrayType.BYTES_PER_ELEMENT : components;
    const total = accessor.count * numElements;

    for (let i = 0, count = 0;  i < total; i += numElements, count += components) {
        for (let j = 0; j < components; j++) {
            dest[count + j] = buffer[i + j] * norm;
        }
    }
}

// Quantizes normalized values onto signed bytes in [-127, 127]. Rounding and clamping are explicit
// because Int8Array assignment truncates and wraps. The destination is wider than the source, and
// the padding component the vertex fetch requires is left at zero.
function setQuantizedArrayData(gltf: GLTF, accessor: GLTFAccessor, dest: Int8Array, buffer: ArrayLike<number>) {
    const ArrayType = GLTF_TO_ARRAY_TYPE[accessor.componentType];
    const norm = getNormalizedScale(ArrayType);

    const bufferView = gltf.json.bufferViews[accessor.bufferView];

    const srcComponents = GLTF_COMPONENTS[accessor.type];
    const components = dest.length / accessor.count;

    const numElements = bufferView.byteStride ? bufferView.byteStride / ArrayType.BYTES_PER_ELEMENT : srcComponents;
    const total = accessor.count * numElements;

    for (let i = 0, count = 0;  i < total; i += numElements, count += components) {
        for (let j = 0; j < srcComponents; j++) {
            const value = buffer[i + j] * norm;
            dest[count + j] = Math.round(Math.min(1, Math.max(-1, value)) * 127);
        }
    }
}

function convertPrimitive(primitive: GLTFPrimitive, gltf: GLTF, textures: Array<ModelTexture>): Mesh {
    const indicesIdx = primitive.indices;

    const attributeMap = primitive.attributes;

    const mesh: Mesh = {} as Mesh;

    mesh.indexArray = new TriangleIndexArray();
    const indexAccessor = gltf.json.accessors[indicesIdx];

    const indexArrayBuffer = getBufferData(gltf, indexAccessor);
    mesh.indexArray.resizeExact(indexAccessor.count / 3);
    mesh.indexArray.uint16.set(indexArrayBuffer);

    // vertices
    mesh.vertexArray = new ModelLayoutArray();

    const positionAccessor = gltf.json.accessors[attributeMap.POSITION];

    const vertexArrayBuffer = getBufferData(gltf, positionAccessor);
    mesh.vertexArray.resizeExact(positionAccessor.count);
    mesh.vertexArray.float32.set(vertexArrayBuffer);
    // bounding box
    mesh.aabb = new Aabb(positionAccessor.min, positionAccessor.max);
    const [minX, minY, minZ] = positionAccessor.min;
    const [maxX, maxY, maxZ] = positionAccessor.max;
    mesh.centroid = [(minX + maxX) * 0.5, (minY + maxY) * 0.5, (minZ + maxZ) * 0.5];

    // colors
    if (attributeMap.COLOR_0 !== undefined) {
        const colorAccessor = gltf.json.accessors[attributeMap.COLOR_0];

        const numElements = GLTF_COMPONENTS[colorAccessor.type];
        const colorArrayBuffer = getBufferData(gltf, colorAccessor);
        mesh.colorArray = numElements === 3 ? new Color3fLayoutArray() : new Color4fLayoutArray();

        mesh.colorArray.resizeExact(colorAccessor.count);
        setArrayData(gltf, colorAccessor, mesh.colorArray.float32, colorArrayBuffer);
    }

    // normals
    if (attributeMap.NORMAL !== undefined) {
        mesh.normalArray = new NormalLayoutArray();

        const normalAccessor = gltf.json.accessors[attributeMap.NORMAL];

        mesh.normalArray.resizeExact(normalAccessor.count);
        const normalArrayBuffer = getBufferData(gltf, normalAccessor);
        setQuantizedArrayData(gltf, normalAccessor, mesh.normalArray.int8, normalArrayBuffer);
    }

    // texcoord
    if (attributeMap.TEXCOORD_0 !== undefined && textures.length > 0) {
        const texcoordAccessor = gltf.json.accessors[attributeMap.TEXCOORD_0];
        const texcoordArrayBuffer = getBufferData(gltf, texcoordAccessor);

        if (texcoordAccessor.normalized && GLTF_TO_ARRAY_TYPE[texcoordAccessor.componentType] === Uint16Array) {
            mesh.texcoordArray = new TexcoordNormalizedLayoutArray();
            mesh.texcoordArray.resizeExact(texcoordAccessor.count);
            setArrayData(gltf, texcoordAccessor, mesh.texcoordArray.uint16, texcoordArrayBuffer, false);
        } else {
            mesh.texcoordArray = new TexcoordLayoutArray();
            mesh.texcoordArray.resizeExact(texcoordAccessor.count);
            setArrayData(gltf, texcoordAccessor, mesh.texcoordArray.float32, texcoordArrayBuffer);
        }
    }

    const isMeshoptCompressed = !!(gltf.json.extensionsUsed && gltf.json.extensionsUsed.includes('EXT_meshopt_compression'));

    // V2 tiles
    if (attributeMap._FEATURE_ID_RGBA4444 !== undefined) {
        const featureAccesor = gltf.json.accessors[attributeMap._FEATURE_ID_RGBA4444];

        if (isMeshoptCompressed) {
            setFeatureData(gltf, featureAccesor, false, mesh);
        }
    }

    // V1 tiles
    if (attributeMap._FEATURE_RGBA4444 !== undefined) {
        const featureAccesor = gltf.json.accessors[attributeMap._FEATURE_RGBA4444];
        setFeatureData(gltf, featureAccesor, !isMeshoptCompressed, mesh);
    }

    mesh.hasFeatureData = !!mesh.featureArray;

    // Material
    const materialIdx = primitive.material;
    const materialDesc: Partial<MaterialDescription> = materialIdx !== undefined ?
        gltf.json.materials[materialIdx] :
        {defined: false};

    mesh.material = convertMaterial(materialDesc, textures);

    return mesh;
}

function convertMeshes(gltf: GLTF, textures: Array<ModelTexture>): Array<Array<Mesh>> {
    const meshes: Mesh[][] = [];
    if (!gltf.json.meshes) return meshes;

    for (const meshDesc of gltf.json.meshes) {
        const primitives: Mesh[] = [];

        for (const primitive of meshDesc.primitives) {
            primitives.push(convertPrimitive(primitive, gltf, textures));
        }
        meshes.push(primitives);
    }
    return meshes;
}

function loadNodeBVH(gltf: GLTF, extData: Record<string, number>, meshIdx: number | undefined): ModelBVH | null {
    const binAccIdx = extData['binaryAccessor'];
    const posMinAccIdx = extData['positionMinAccessor'];
    const posMaxAccIdx = extData['positionMaxAccessor'];
    const idxAccIdx = extData['indexAccessor'];

    if (!Number.isInteger(binAccIdx) || !Number.isInteger(posMinAccIdx) ||
        !Number.isInteger(posMaxAccIdx) || !Number.isInteger(idxAccIdx)) return null;

    const accessors = gltf.json.accessors;
    if (binAccIdx >= accessors.length || posMinAccIdx >= accessors.length ||
        posMaxAccIdx >= accessors.length || idxAccIdx >= accessors.length) return null;

    const binAcc = accessors[binAccIdx];
    const posMinAcc = accessors[posMinAccIdx];
    const posMaxAcc = accessors[posMaxAccIdx];
    const idxAcc = accessors[idxAccIdx];

    if (binAcc.bufferView === undefined || posMinAcc.bufferView === undefined ||
        posMaxAcc.bufferView === undefined || idxAcc.bufferView === undefined) return null;

    const bv = gltf.json.bufferViews;
    if (binAcc.bufferView >= bv.length || posMinAcc.bufferView >= bv.length ||
        posMaxAcc.bufferView >= bv.length || idxAcc.bufferView >= bv.length) return null;

    const binBV = bv[binAcc.bufferView];
    const posMinBV = bv[posMinAcc.bufferView];
    const posMaxBV = bv[posMaxAcc.bufferView];
    const idxBV = bv[idxAcc.bufferView];

    if (binBV.buffer >= gltf.buffers.length || posMinBV.buffer >= gltf.buffers.length ||
        posMaxBV.buffer >= gltf.buffers.length || idxBV.buffer >= gltf.buffers.length) return null;

    // Validate each accessor's [byteOffset, byteOffset + dataSize) lies within its buffer view.
    const fits = (accByteOffset: number, dataSize: number, viewLen: number) => accByteOffset >= 0 && accByteOffset <= viewLen && dataSize <= viewLen - accByteOffset;

    const binAccOffset = binAcc.byteOffset || 0;
    const posMinAccOffset = posMinAcc.byteOffset || 0;
    const posMaxAccOffset = posMaxAcc.byteOffset || 0;
    const idxAccOffset = idxAcc.byteOffset || 0;

    if (!fits(binAccOffset, 0, binBV.byteLength) ||
        !fits(posMinAccOffset, posMinAcc.count * 3 * 4, posMinBV.byteLength) ||
        !fits(posMaxAccOffset, posMaxAcc.count * 3 * 4, posMaxBV.byteLength) ||
        !fits(idxAccOffset, idxAcc.count * 4, idxBV.byteLength)) return null;

    const binData = new Uint8Array(gltf.buffers[binBV.buffer], (binBV.byteOffset || 0) + binAccOffset, binBV.byteLength - binAccOffset);

    const posMinData = new Float32Array(gltf.buffers[posMinBV.buffer], (posMinBV.byteOffset || 0) + posMinAccOffset, posMinAcc.count * 3);

    const posMaxData = new Float32Array(gltf.buffers[posMaxBV.buffer], (posMaxBV.byteOffset || 0) + posMaxAccOffset, posMaxAcc.count * 3);

    const idxData = new Uint32Array(gltf.buffers[idxBV.buffer], (idxBV.byteOffset || 0) + idxAccOffset, idxAcc.count);

    const bvh = new ModelBVH();
    bvh.serializeFromGltf(binData, posMinData, posMaxData, idxData);

    // Set vertex positions from the mesh's POSITION accessor
    if (meshIdx !== undefined && gltf.json.meshes && gltf.json.meshes[meshIdx]) {
        const primitive = gltf.json.meshes[meshIdx].primitives[0];
        if (primitive && primitive.attributes.POSITION !== undefined &&
            primitive.attributes.POSITION < accessors.length) {
            const posAcc = accessors[primitive.attributes.POSITION];
            if (posAcc.bufferView !== undefined && posAcc.bufferView < bv.length) {
                const posBV = bv[posAcc.bufferView];
                if (posBV.buffer < gltf.buffers.length) {
                    const posAccOffset = posAcc.byteOffset || 0;
                    const stride = posBV.byteStride ? posBV.byteStride / 4 : 3;
                    const needed = posAcc.count * stride * 4;
                    if (fits(posAccOffset, needed, posBV.byteLength)) {
                        const byteOffset = (posBV.byteOffset || 0) + posAccOffset;
                        if (stride === 3) {
                            bvh.setVertices(new Float32Array(gltf.buffers[posBV.buffer], byteOffset, posAcc.count * 3));
                        } else {
                            const src = new Float32Array(gltf.buffers[posBV.buffer], byteOffset, posAcc.count * stride);
                            const vertices = new Float32Array(posAcc.count * 3);
                            for (let i = 0; i < posAcc.count; i++) {
                                vertices[i * 3] = src[i * stride];
                                vertices[i * 3 + 1] = src[i * stride + 1];
                                vertices[i * 3 + 2] = src[i * stride + 2];
                            }
                            bvh.setVertices(vertices);
                        }
                    }
                }
            }
        }
    }

    return bvh;
}

function convertNode(nodeDesc: GLTFNode, gltf: GLTF, meshes: Array<Array<Mesh>>): ModelNode {
    const {matrix, rotation, translation, scale, mesh, extras, children, name} = nodeDesc;
    const node = {} as ModelNode;
    node.name = name;
    node.localMatrix = matrix || mat4.fromRotationTranslationScale([], rotation || [0, 0, 0, 1], translation || [0, 0, 0], scale || [1, 1, 1]);
    node.globalMatrix = mat4.clone(node.localMatrix);
    if (mesh !== undefined) {
        node.meshes = meshes[mesh];
        const anchor: vec2 = node.anchor = [0, 0];
        for (const mesh of node.meshes) {
            const {min, max} = mesh.aabb;
            anchor[0] += min[0] + max[0];
            anchor[1] += min[1] + max[1];
        }
        anchor[0] = Math.floor(anchor[0] / node.meshes.length / 2);
        anchor[1] = Math.floor(anchor[1] / node.meshes.length / 2);
    }

    if (extras) {
        if (extras.id) {
            node.id = extras.id as string;
        }

        if (extras.lights) {
            node.lights = decodeLights(extras.lights as string);
        }
        if (extras['MAPBOX_geometry_bloom']) {
            node.isGeometryBloom = extras['MAPBOX_geometry_bloom'] as boolean;
        }
        if (extras['MAPBOX_zoom_min']) {
            node.minZoom = extras['MAPBOX_zoom_min'] as number;
        }
        if (extras['MAPBOX_zoom_max']) {
            node.maxZoom = extras['MAPBOX_zoom_max'] as number;
        }
    }

    if (children) {
        const converted: ModelNode[] = [];
        for (const childNodeIdx of children) {
            converted.push(convertNode(gltf.json.nodes[childNodeIdx], gltf, meshes));
        }
        node.children = converted;
    }

    if (nodeDesc.extensions && nodeDesc.extensions['mbx_bvh']) {
        node.meshBVH = loadNodeBVH(gltf, nodeDesc.extensions['mbx_bvh'] as Record<string, number>, mesh);
    }

    // Propagate meshBVH from children if this node has none
    if (!node.meshBVH && node.children) {
        for (const child of node.children) {
            if (child.meshBVH) {
                node.meshBVH = child.meshBVH;
                break;
            }
        }
    }

    return node;
}

type FootprintMesh = {
    vertices: Array<Point>;
    indices: Array<number>;
};

function convertFootprint(mesh: FootprintMesh): Footprint | null | undefined {
    if (mesh.vertices.length === 0 || mesh.indices.length === 0) {
        return null;
    }

    // Use a fixed size triangle grid (8x8 cells) for acceleration intersection queries
    // with an exception that the cell size should never be larger than 256 tile units
    // (equals to 32x32 subdivision).
    const grid = new TriangleGridIndex(mesh.vertices, mesh.indices, 8, 256);
    const [min, max] = [grid.min.clone(), grid.max.clone()];

    return {
        vertices: mesh.vertices,
        indices: mesh.indices,
        grid,
        min,
        max
    };
}

function parseLegacyFootprintMesh(gltfNode: GLTFNode): FootprintMesh | null | undefined {
    if (!gltfNode.extras || !gltfNode.extras.ground) {
        return null;
    }

    const groundContainer = gltfNode.extras.ground as number[][][];
    if (!groundContainer || !Array.isArray(groundContainer) || groundContainer.length === 0) {
        return null;
    }

    const ground = groundContainer[0];
    if (!ground || !Array.isArray(ground) || ground.length === 0) {
        return null;
    }

    // Populate only the vertex list of the footprint mesh.
    const vertices: Array<Point> = [];

    for (const point of ground) {
        if (!Array.isArray(point) || point.length !== 2) {
            continue;
        }

        const x = point[0];
        const y = point[1];

        if (typeof x !== "number" || typeof y !== "number") {
            continue;
        }

        vertices.push(new Point(x, y));
    }

    if (vertices.length < 3) {
        return null;
    }

    if (vertices.length > 1 && vertices.at(-1).equals(vertices[0])) {
        vertices.pop();
    }

    // Ensure that the vertex list is defined in CW order
    let cross = 0;

    for (let i = 0; i < vertices.length; i++) {
        const a = vertices[i];
        const b = vertices[(i + 1) % vertices.length];
        const c = vertices[(i + 2) % vertices.length];

        cross += (a.x - b.x) * (c.y - b.y) - (c.x - b.x) * (a.y - b.y);
    }

    if (cross > 0) {
        vertices.reverse();
    }

    // Triangulate the footprint and compute grid acceleration structure for
    // more performant intersection queries.
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call
    const indices: number[] = earcut(vertices.flatMap(v => [v.x, v.y]), []);

    if (indices.length === 0) {
        return null;
    }

    return {vertices, indices};
}

function parseNodeFootprintMesh(meshes: Array<Mesh>, matrix: mat4): FootprintMesh | null | undefined {
    const vertices: Array<Point> = [];
    const indices: Array<number> = [];

    let baseVertex = 0;

    const tempVertex: number[] = [];
    for (const mesh of meshes) {
        baseVertex = vertices.length;

        const vArray = mesh.vertexArray.float32;
        const iArray = mesh.indexArray.uint16;

        for (let i = 0; i < mesh.vertexArray.length; i++) {
            tempVertex[0] = vArray[i * 3 + 0];
            tempVertex[1] = vArray[i * 3 + 1];
            tempVertex[2] = vArray[i * 3 + 2];
            vec3.transformMat4(tempVertex, tempVertex, matrix);
            vertices.push(new Point(tempVertex[0], tempVertex[1]));
        }

        for (let i = 0; i < mesh.indexArray.length * 3; i++) {
            indices.push(iArray[i] + baseVertex);
        }
    }

    if (indices.length % 3 !== 0) {
        return null;
    }

    for (let i = 0; i < indices.length; i += 3) {
        const a = vertices[indices[i + 0]];
        const b = vertices[indices[i + 1]];
        const c = vertices[indices[i + 2]];

        if ((a.x - b.x) * (c.y - b.y) - (c.x - b.x) * (a.y - b.y) > 0) {
            [indices[i + 1], indices[i + 2]] = [indices[i + 2], indices[i + 1]];
        }
    }

    return {vertices, indices};
}

function convertFootprints(convertedNodes: ModelNode[], sceneNodes: number[], modelNodes: GLTFNode[]) {
    // modelNodes == a list of nodes in the gltf file
    // sceneNodes == an index array pointing to modelNodes being parsed
    assert(convertedNodes.length === sceneNodes.length);

    // Two different footprint formats are supported:
    //  1) Legacy format where footprints are defined as a linestring json
    //     inside extras-section of the node.
    //  2) Version "1" where footprints are included as regular gltf meshes and
    //     connected to correct models via matching ids.

    // Find footprint-only nodes from the list.
    const nodeFootprintLookup: Record<string, number> = {};
    const footprintNodeIndices = new Set<number>();

    for (let i = 0; i < convertedNodes.length; i++) {
        const gltfNode = modelNodes[sceneNodes[i]];

        if (!gltfNode.extras) {
            continue;
        }

        const fpVersion = gltfNode.extras["mapbox:footprint:version"] as string;
        const fpId = gltfNode.extras["mapbox:footprint:id"] as string;

        if (fpVersion || fpId) {
            footprintNodeIndices.add(i);
        }

        if (fpVersion !== "1.0.0" || !fpId) {
            continue;
        }

        nodeFootprintLookup[fpId] = i;
    }

    // Go through nodes and see if either of the supported footprint formats are defined
    for (let i = 0; i < convertedNodes.length; i++) {
        if (footprintNodeIndices.has(i)) {
            continue;
        }

        const node = convertedNodes[i];

        const gltfNode = modelNodes[sceneNodes[i]];

        if (!gltfNode.extras) {
            continue;
        }

        // Prefer footprint nodes over the legacy format
        let fpMesh: FootprintMesh | null | undefined = null;

        if (node.id in nodeFootprintLookup) {
            fpMesh = parseNodeFootprintMesh(convertedNodes[nodeFootprintLookup[node.id]].meshes, node.localMatrix);
        }

        if (!fpMesh) {
            fpMesh = parseLegacyFootprintMesh(gltfNode);
        }

        if (fpMesh) {
            node.footprint = convertFootprint(fpMesh);
        }
    }

    // Remove footprint nodes as they serve no other purpose
    if (footprintNodeIndices.size > 0) {
        const nodesToRemove: number[] = Array.from(footprintNodeIndices.values()).sort((a, b) => a - b);

        for (let i = nodesToRemove.length - 1; i >= 0; i--) {
            convertedNodes.splice(nodesToRemove[i], 1);
        }
    }
}

function findScene(scenes: Array<{name?: string; nodes: number[]}>, name: string): number {
    for (let i = 0; i < scenes.length; i++) {
        if (scenes[i].name === name) return i;
    }
    return -1;
}

export default function convertModel(gltf: GLTF): Array<ModelNode> {
    const textures = convertTextures(gltf, gltf.images);
    const meshes = convertMeshes(gltf, textures);

    const {scenes, scene, nodes} = gltf.json;

    // Find "Default Scene" by name; fall back to the GLTF default scene index
    let sceneIndex = scenes ? findScene(scenes, "Default Scene") : -1;
    if (sceneIndex < 0) sceneIndex = scene || 0;
    const sceneNodes: number[] = scenes && scenes[sceneIndex] ? scenes[sceneIndex].nodes || [] : [...nodes.keys()];

    const resultNodes: ModelNode[] = [];
    for (const nodeIdx of sceneNodes) {
        resultNodes.push(convertNode(nodes[nodeIdx], gltf, meshes));
    }

    convertFootprints(resultNodes, sceneNodes, gltf.json.nodes);

    // Find "LOD" scene by name; match nodes to primary scene counterparts via extras.id
    const lodSceneIndex = scenes ? findScene(scenes, "LOD") : -1;
    if (lodSceneIndex >= 0) {
        const lodSceneNodes = scenes[lodSceneIndex].nodes;
        const lodNodeById: Map<string, ModelNode> = new Map();
        for (const nodeIdx of lodSceneNodes) {
            const lodNode = convertNode(nodes[nodeIdx], gltf, meshes);
            if (lodNode.id) {
                lodNodeById.set(lodNode.id, lodNode);
            }
        }
        for (const node of resultNodes) {
            if (node.id) {
                const lodNode = lodNodeById.get(node.id);
                if (lodNode && lodNode.meshes) {
                    node.lodMeshes = lodNode.meshes;
                    if (lodNode.meshBVH) {
                        node.meshBVH = lodNode.meshBVH;
                    }
                }
            }
        }
    }

    return resultNodes;
}

// Fetches a glTF, converts it, and builds a Model. Reached from core `ModelManager` through the
// `Standard` facade so that the glTF/draco/meshopt loaders stay out of core.
export async function loadModel(requestParameters: RequestParameters, id: string, url: string): Promise<Model> {
    const gltf = await loadGLTF(requestParameters);
    const model = new Model(id, url, undefined, undefined, convertModel(gltf));
    model.computeBoundsAndApplyParent();
    return model;
}

export function process3DTile(gltf: GLTF, zScale: number): Array<ModelNode> {
    const nodes = convertModel(gltf);
    for (const node of nodes) {
        // A node without its own BVH picking mesh (own extension, or inherited from a child or LOD
        // counterpart can't skip the expensive heightmap bake, otherwise there would be no height
        // info for this model
        if (!node.meshBVH) {
            for (const mesh of node.meshes) {
                parseHeightmap(mesh);
            }
        }
        if (node.lights) {
            // The door lights borrow the door part's style: the vertex color they blend the styled
            // color over, and the bounds their emissive height gradient resolves against. The last
            // mesh carrying door geometry wins, as it did when this was recomputed per evaluation.
            let doorVertexColor = 0xffff;
            for (const mesh of node.meshes) {
                if (mesh.doorVertexColor !== undefined) {
                    doorVertexColor = mesh.doorVertexColor;
                    node.lightsStyleAabb = mesh.aabb;
                }
            }
            node.lightMeshIndex = node.meshes.length;
            node.meshes.push(createLightsMesh(node.lights, zScale, doorVertexColor));
        }
    }
    return nodes;
}

function parseHeightmap(mesh: Mesh) {
    // This is a temporary, best effort approach, implementation that's to be removed, for Mapbox landmarks,
    // by a implementation in tiler. It would eventually still be used for 3d party models.
    mesh.heightmap = new Float32Array(HEIGHTMAP_DIM * HEIGHTMAP_DIM);
    mesh.heightmap.fill(-1);

    const vertices = mesh.vertexArray.float32;
    // implementation assumes tile coordinates for x and y and -1 and later +2
    // are to prevent going out of range
    const xMin = mesh.aabb.min[0] - 1;
    const yMin = mesh.aabb.min[1] - 1;
    const xMax = mesh.aabb.max[0];
    const yMax = mesh.aabb.max[1];
    const xRange = xMax - xMin + 2;
    const yRange = yMax - yMin + 2;
    const xCellInv = HEIGHTMAP_DIM / xRange;
    const yCellInv = HEIGHTMAP_DIM / yRange;

    for (let i = 0; i < vertices.length; i += 3) {
        const px = vertices[i + 0];
        const py = vertices[i + 1];
        const pz = vertices[i + 2];
        const x = ((px - xMin) * xCellInv) | 0;
        const y = ((py - yMin) * yCellInv) | 0;
        assert(x >= 0 && x < HEIGHTMAP_DIM);
        assert(y >= 0 && y < HEIGHTMAP_DIM);
        if (pz > mesh.heightmap[y * HEIGHTMAP_DIM + x]) {
            mesh.heightmap[y * HEIGHTMAP_DIM + x] = pz;
        }
    }
}

export function calculateLightsMesh(lights: Array<AreaLight>, zScale: number, indexArray: TriangleIndexArray, vertexArray: ModelLayoutArray, colorArray: Color4fLayoutArray) {
    indexArray.reserve(indexArray.length + 4 * lights.length);
    vertexArray.reserve(vertexArray.length + 10 * lights.length);
    colorArray.reserve(colorArray.length + 10 * lights.length);

    let currentVertex = vertexArray.length;
    // Model layer color4 attribute is used for light offset: first three components are light's offset in tile space (z
    // also in tile space) and 4th parameter is a decimal number that carries 2 parts: the distance to full light
    // falloff is in the integer part, and the decimal part represents the ratio of distance where the falloff starts (saturated
    // until it reaches that part).
    for (const light of lights) {
        // fallOff - light range from the door.
        const fallOff = Math.min(10, Math.max(4, 1.3 * light.height)) * zScale;
        const tangent = [-light.normal[1], light.normal[0], 0];
        // 0---3  (at light.height above light.points)
        // |   |
        // 1-p-2  (p for light.position at bottom edge)

        // horizontalSpread is tangent of the angle between light geometry and light normal.
        // Cap it for doors with large inset (depth) to prevent intersecting door posts.
        const horizontalSpread = Math.min(0.29, 0.1 * light.width / light.depth);
        // A simple geometry, that starts at door, starts towards the centre of door to prevent intersecting
        // door posts. Later, additional vertices at depth distance from door could be reconsidered.
        // 0.01f to prevent intersection with door post.
        const width = light.width - 2 * light.depth * zScale * (horizontalSpread + 0.01);
        const v1 = vec3.scaleAndAdd([], light.pos, tangent, width / 2);
        const v2 = vec3.scaleAndAdd([], light.pos, tangent, -width / 2);
        const v0 = [v1[0], v1[1], v1[2] + light.height];
        const v3 = [v2[0], v2[1], v2[2] + light.height];

        const v1extrusion = vec3.scaleAndAdd([], light.normal, tangent, horizontalSpread);
        vec3.scale(v1extrusion, v1extrusion, fallOff);
        const v2extrusion = vec3.scaleAndAdd([], light.normal, tangent, -horizontalSpread);
        vec3.scale(v2extrusion, v2extrusion, fallOff);

        vec3.add(v1extrusion, v1, v1extrusion);
        vec3.add(v2extrusion, v2, v2extrusion);

        v1[2] += 0.1;
        v2[2] += 0.1;
        vertexArray.emplaceBack(v1extrusion[0], v1extrusion[1], v1extrusion[2]);
        vertexArray.emplaceBack(v2extrusion[0], v2extrusion[1], v2extrusion[2]);
        vertexArray.emplaceBack(v1[0], v1[1], v1[2]);
        vertexArray.emplaceBack(v2[0], v2[1], v2[2]);
        // side: top
        vertexArray.emplaceBack(v0[0], v0[1], v0[2]);
        vertexArray.emplaceBack(v3[0], v3[1], v3[2]);
        // side
        vertexArray.emplaceBack(v1[0], v1[1], v1[2]);
        vertexArray.emplaceBack(v2[0], v2[1], v2[2]);
        vertexArray.emplaceBack(v1extrusion[0], v1extrusion[1], v1extrusion[2]);
        vertexArray.emplaceBack(v2extrusion[0], v2extrusion[1], v2extrusion[2]);
        // Light doesnt include light coordinates - instead it includes offset to light area segment. Distances are
        // normalized by dividing by fallOff. Normalized lighting coordinate system is used where center of
        // coord system is on half of door and +Y is direction of extrusion.
        // z includes half width - this is used to calculate distance to segment.

        // 2 and 3 are bottom of the door, fully lit.
        const halfWidth = width / fallOff / 2.0;
        // right ground extruded looking out from door
        // x Coordinate is used to model angle (for spot)
        colorArray.emplaceBack(-halfWidth - horizontalSpread, -1, halfWidth, 0.8);
        colorArray.emplaceBack(halfWidth + horizontalSpread, -1, halfWidth, 0.8);
        // keep shine at bottom of door even for reduced emissive strength
        colorArray.emplaceBack(-halfWidth, 0, halfWidth, 1.3);
        colorArray.emplaceBack(halfWidth, 0, halfWidth, 1.3);
        // for all vertices on the side, push the light origin behind the door top
        colorArray.emplaceBack(halfWidth + horizontalSpread, -0.8, halfWidth, 0.7);
        colorArray.emplaceBack(halfWidth + horizontalSpread, -0.8, halfWidth, 0.7);
        // side at door, ground
        colorArray.emplaceBack(0, 0, halfWidth, 1.3);
        colorArray.emplaceBack(0, 0, halfWidth, 1.3);
        // extruded side
        colorArray.emplaceBack(halfWidth + horizontalSpread, -1.2, halfWidth, 0.8);
        colorArray.emplaceBack(halfWidth + horizontalSpread, -1.2, halfWidth, 0.8);

        // Finally, the triangle indices
        indexArray.emplaceBack(6 + currentVertex, 4 + currentVertex, 8 + currentVertex);
        indexArray.emplaceBack(7 + currentVertex, 9 + currentVertex, 5 + currentVertex);
        indexArray.emplaceBack(0 + currentVertex, 1 + currentVertex, 2 + currentVertex);
        indexArray.emplaceBack(1 + currentVertex, 3 + currentVertex, 2 + currentVertex);
        currentVertex += 10;
    }
}

function createLightsMesh(lights: Array<AreaLight>, zScale: number, doorVertexColor: number): Mesh {
    const mesh = {} as Mesh;
    mesh.indexArray = new TriangleIndexArray();
    mesh.vertexArray = new ModelLayoutArray();
    mesh.colorArray = new Color4fLayoutArray();

    // calculateLightsMesh emits 4 triangles, 10 vertices, and 10 colors per light;
    // pre-size exactly so so we don't overallocate for a known size
    mesh.indexArray.reserveExact(lights.length * 4);
    mesh.vertexArray.reserveExact(lights.length * 10);
    mesh.colorArray.reserveExact(lights.length * 10);

    calculateLightsMesh(lights, zScale, mesh.indexArray, mesh.vertexArray, mesh.colorArray);

    // The door lights are drawn with the evaluated style of the door part, which they select by
    // carrying its part id on every vertex, blended over the door geometry's own vertex color so
    // that model-color-mix-intensity resolves the same way it does on the door itself.
    mesh.featureArray = new FeatureVertexArray();
    mesh.featureArray.reserveExact(mesh.vertexArray.length);
    for (let i = 0; i < mesh.vertexArray.length; i++) {
        mesh.featureArray.emplaceBack(doorVertexColor, PartIndices.door);
    }
    mesh.hasFeatureData = true;

    const material = {} as Material;
    material.defined = true;
    material.emissiveFactor = Color.black;
    const pbrMetallicRoughness = {} as PbrMetallicRoughness;
    pbrMetallicRoughness.baseColorFactor = Color.white;
    material.pbrMetallicRoughness = pbrMetallicRoughness;
    mesh.material = material;
    mesh.aabb = new Aabb([Infinity, Infinity, Infinity], [-Infinity, -Infinity, -Infinity]);
    return mesh;
}

function decodeLights(base64: string): Array<AreaLight> {
    if (!base64.length) return [];
    const decoded = base64DecToArr(base64);
    const lights: AreaLight[] = [];
    const lightCount = decoded.length / 24; // 24 bytes (4 uint16 & 4 floats) per light
    // Each door light is defined by two endpoiunts in tile coordinates, left and bottom right
    // (where normal is implied from those two), depth and height.
    // https://github.com/mapbox/mapbox-3dtile-tools/pull/100
    const lightData = new Uint16Array(decoded.buffer);
    const lightDataFloat = new Float32Array(decoded.buffer);
    const stride = 6;
    for (let i = 0; i < lightCount; i++) {
        const height = lightData[i * 2 * stride] / 30;
        const elevation = lightData[i * 2 * stride + 1] / 30;
        const depth = lightData[i * 2 * stride + 10] / 100;
        const x0 = lightDataFloat[i * stride + 1];
        const y0 = lightDataFloat[i * stride + 2];
        const x1 = lightDataFloat[i * stride + 3];
        const y1 = lightDataFloat[i * stride + 4];
        const dx = x1 - x0;
        const dy = y1 - y0;
        const width = Math.hypot(dx, dy);
        const normal: [number, number, number] = [dy / width, -dx / width, 0];
        const pos: [number, number, number] = [x0 + dx * 0.5, y0 + dy * 0.5, elevation];
        const points: [number, number, number, number] = [x0, y0, x1, y1];
        lights.push({pos, normal, width, height, depth, points});
    }
    return lights;
}
